import styled, { css, keyframes } from 'styled-components';

const pulse = (color: string) => keyframes`
  0% {
    transform: scale(0.9);
  }
  70% {
    transform: scale(1);
    box-shadow: ${color} 0 0 0 15px;
  }
  100% {
    transform: scale(0.9);
    box-shadow: ${color} 0 0 0 0;
  }
`;

export const SocialButtonCommonStyles = css<{ shouldPulse?: boolean }>`
  align-items: center;
  display: flex;
  color: ${({ theme }) => theme.colors.white};
  font-family: ${({ theme }) => theme.fonts.familyUbuntu};
  font-size: ${({ theme }) => theme.fonts.sizes.default};
  text-decoration: none;

  &:hover {
    filter: brightness(1.2);
  }

  &[data-social='twitter-x'],
  &[data-social='twitter'] {
    background-color: black;
    border-color: black;

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
        animation: ${pulse('rgb(0, 0, 0, 0)')} 1.5s infinite;
        box-shadow: rgb(0, 0, 0, 0.5) 0 0 0 0;
       `};

    &:hover {
      background-color: black;
      color: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-social='facebook'] {
    background-color: ${({ theme }) => theme.colors.facebook.main};
    border-color: ${({ theme }) => theme.colors.facebook.main};

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
         animation: ${pulse('rgb(66, 103, 178, 0)')} 1.5s infinite;
         box-shadow: rgb(66, 103, 178, 0.5) 0 0 0 0;
       `};

    &:hover {
      background-color: ${({ theme }) => theme.colors.facebook.main};
      color: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-social='linkedin'] {
    background-color: ${({ theme }) => theme.colors.linkedIn.main};
    border-color: ${({ theme }) => theme.colors.linkedIn.main};

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
         animation: ${pulse('rgb(0, 119, 181, 0)')} 1.5s infinite;
         box-shadow: rgb(0, 119, 181, 0.5) 0 0 0 0;
       `};

    &:hover {
      background-color: ${({ theme }) => theme.colors.linkedIn.main};
      color: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-social='whatsapp'] {
    background-color: #25D366;
    border-color: #25D366;
    color: ${({ theme }) => theme.colors.black};

    svg path {
      fill: ${({ theme }) => theme.colors.black};
    }

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
         animation: ${pulse('rgb(37, 211, 102, 0)')} 1.5s infinite;
         box-shadow: rgb(37, 211, 102, 0.5) 0 0 0 0;
       `};

    &:hover {
      background-color: #25D366;
    }
  }

  &[data-social='telegram'] {
    background-color: rgba(0, 136, 204, 1);
    border-color: rgba(0, 136, 204, 1);

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
         animation: ${pulse('rgb(66, 103, 178, 0)')} 1.5s infinite;
         box-shadow: rgb(66, 103, 178, 0.5) 0 0 0 0;
       `};

    &:hover {
      background-color: rgba(0, 136, 204, 1);
      color: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-social='reddit'] {
    background-color: rgba(255, 87, 0, 1);
    border-color: rgba(255, 87, 0, 1);

    ${({ shouldPulse }) =>
      shouldPulse &&
      css`
         animation: ${pulse('rgb(255, 87, 0, 0)')} 1.5s infinite;
         box-shadow: rgb(255, 87, 0, 0.5) 0 0 0 0;
       `};

    &:hover {
    background-color: rgba(255, 87, 0, 1);
      color: ${({ theme }) => theme.colors.white};
    }
  }

  &[data-social='copy'] {
    border-color: ${({ theme }) => theme.colors.grey[300]};
    color: ${({ theme }) => theme.colors.text.main};

    svg path {
      fill: ${({ theme }) => theme.colors.text.main};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.button.secondary.hover};
      border-color: ${({ theme }) => theme.colors.grey[600]};
      color: ${({ theme }) => theme.colors.black};
    
      svg path {
        fill: ${({ theme }) => theme.colors.black};
      } 
    }
  }

  ${({ shouldPulse }) =>
    shouldPulse &&
    css`
        &:hover {
         animation: none;
       }
     `};
`;

export const StyledSocialButton = styled.a<{ $isDisabled?: boolean; shouldPulse?: boolean }>`
  ${SocialButtonCommonStyles};
  border-radius: 0.5rem;
  gap: 0.5rem;
  height: 3.6rem;
  justify-content: center;
  padding: 0.9rem 1.6rem;
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'auto')};

  svg {
    width: 1.3rem;
    height: 1.4rem;
    margin: 0;

    path {
      fill: ${({ theme }) => theme.colors.white};
    }
  }

  ${({ $isDisabled, theme }) =>
    $isDisabled &&
    `
    border-color: ${theme.colors.grey[400]};
    cursor: not-allowed;
    background-color: ${theme.colors.grey[400]} !important;
    color: ${theme.colors.grey[600]} !important;

    svg path {
      fill: ${theme.colors.grey[600]} !important;
    }

    &:hover {
      background-color: ${theme.colors.grey[400]} !important;
      color: ${theme.colors.grey[600]} !important;
    }
  `}
`;

export const StyledMiniSocialButton = styled(StyledSocialButton)<{ hasBorder?: boolean }>`
  width: 3rem;
  height: 3rem;
  border-radius: 100%;
  padding: 0;

  ${({ hasBorder, theme }) =>
    hasBorder &&
    css`
    border-style: solid;
    border-width: 1px;
    border-color: ${theme.colors.button.secondary.hover};
  `}

  &[data-social='telegram'] {
    svg {
      width: 2.4rem;
      height: 2.4rem;
    }
  }
`;
